import { useState } from 'react';
import { SpaceBetween, Button, Alert, Box } from '@cloudscape-design/components';
import { GuidedForm } from './GuidedForm';
import { JsonEditor } from './JsonEditor';
import { getTemplate } from '../templates/registry';

export type EditorMode = 'form' | 'json';

interface FormJsonToggleProps {
  configType: string;
  formValues: Record<string, unknown>;
  onFormValuesChange: (values: Record<string, unknown>) => void;
  configName: string;
  onConfigNameChange: (name: string) => void;
  configId: string;
  onConfigIdChange: (id: string) => void;
  showConfigTypeSelector?: boolean;
  onConfigTypeChange?: (type: string) => void;
  errors?: Record<string, string>;
}

/**
 * Builds a full ConfigurationObject JSON from form values.
 */
function buildJsonFromForm(
  configId: string,
  configName: string,
  configType: string,
  formValues: Record<string, unknown>
): string {
  // Separate internal fields from regular payload fields
  const { _rawPayload, _businessUnit, ...payloadFields } = formValues;

  let payload: Record<string, unknown>;
  if (typeof _rawPayload === 'string') {
    try {
      payload = JSON.parse(_rawPayload);
    } catch {
      payload = {};
    }
  } else {
    payload = payloadFields;
  }

  const obj = { configId, configName, configType, payload };
  return JSON.stringify(obj, null, 2);
}

/**
 * Parses JSON back into form-compatible values.
 * Returns the parsed values and any unrecognised fields.
 */
function parseJsonToForm(
  jsonText: string,
  configType: string
): { values: Record<string, unknown>; configName: string; configId: string; unrecognisedFields: string[] } {
  const parsed = JSON.parse(jsonText);
  const template = getTemplate(configType);
  const knownKeys = new Set(template?.payloadSchema.map((f) => f.key) ?? []);

  const payload = parsed.payload ?? {};
  const unrecognisedFields: string[] = [];

  for (const key of Object.keys(payload)) {
    if (!knownKeys.has(key) && knownKeys.size > 0) {
      unrecognisedFields.push(key);
    }
  }

  return {
    values: payload,
    configName: parsed.configName ?? '',
    configId: parsed.configId ?? '',
    unrecognisedFields,
  };
}

/**
 * Component providing a toggle between Guided Form and JSON Editor.
 * Handles bidirectional serialization between the two modes.
 *
 * Requirements: 3.11, 3.12, 3.13, 3.14, 4.2, 4.3, 4.4
 */
export function FormJsonToggle({
  configType,
  formValues,
  onFormValuesChange,
  configName,
  onConfigNameChange,
  configId,
  onConfigIdChange,
  showConfigTypeSelector,
  onConfigTypeChange,
  errors = {},
}: FormJsonToggleProps) {
  const [mode, setMode] = useState<EditorMode>('form');
  const [jsonText, setJsonText] = useState('');
  const [switchWarning, setSwitchWarning] = useState<string[] | null>(null);

  const handleSwitchToJson = () => {
    const json = buildJsonFromForm(configId, configName, configType, formValues);
    setJsonText(json);
    setSwitchWarning(null);
    setMode('json');
  };

  const handleJsonChange = (text: string) => {
    setJsonText(text);
    // Live-sync parsed values back to parent so "Review & Save" works from JSON mode
    try {
      const parsed = JSON.parse(text);
      if (parsed.configId && typeof parsed.configId === 'string') {
        onConfigIdChange(parsed.configId);
      }
      if (parsed.configName && typeof parsed.configName === 'string') {
        onConfigNameChange(parsed.configName);
      }
      if (parsed.payload && typeof parsed.payload === 'object') {
        onFormValuesChange(parsed.payload as Record<string, unknown>);
      }
    } catch {
      // Invalid JSON — don't sync, user is still typing
    }
  };

  const handleSwitchToForm = () => {
    try {
      const { values, configName: parsedName, configId: parsedId, unrecognisedFields } =
        parseJsonToForm(jsonText, configType);

      if (unrecognisedFields.length > 0) {
        setSwitchWarning(unrecognisedFields);
      } else {
        setSwitchWarning(null);
      }

      onFormValuesChange(values);
      onConfigNameChange(parsedName);
      onConfigIdChange(parsedId);
      setMode('form');
    } catch (e) {
      // JSON is invalid — can't switch to form
      setSwitchWarning(null);
    }
  };

  const dismissWarning = () => {
    setSwitchWarning(null);
  };

  return (
    <SpaceBetween size="m">
      <Box float="right">
        {mode === 'form' ? (
          <Button iconName="edit" onClick={handleSwitchToJson}>
            Switch to JSON
          </Button>
        ) : (
          <Button iconName="view-full" onClick={handleSwitchToForm}>
            Switch to Form
          </Button>
        )}
      </Box>

      {switchWarning && switchWarning.length > 0 && (
        <Alert type="warning" dismissible onDismiss={dismissWarning}>
          The JSON contains fields not in the form schema for "{configType}":{' '}
          <strong>{switchWarning.join(', ')}</strong>. These fields will be preserved in the payload
          but won't appear as form inputs.
        </Alert>
      )}

      {mode === 'form' ? (
        <GuidedForm
          configType={configType}
          values={formValues}
          onChange={onFormValuesChange}
          configName={configName}
          onConfigNameChange={onConfigNameChange}
          configId={configId}
          onConfigIdChange={onConfigIdChange}
          showConfigTypeSelector={showConfigTypeSelector}
          onConfigTypeChange={onConfigTypeChange}
          errors={errors}
        />
      ) : (
        <JsonEditor value={jsonText} onChange={handleJsonChange} />
      )}
    </SpaceBetween>
  );
}

export { buildJsonFromForm, parseJsonToForm };
